"use client";

import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";

import type { OverlayMenuBaseProps } from "@/app/components/menu/overlayMenuSystem";
import Preloader from "@/app/components/preloader";
import QuantityPicker from "@/components/design-system/quantity-picker";
import { FilterMenu, NoMatchingSellers, SellerOptionsSort } from "./sellerOptionsControls";
import { getFilterOrder } from "./sellerOptions";
import { OfferList, ResultCount, useIsSignedIn } from "./sellerOptionsTable";
import useSellerOptions from "./useSellerOptions";
import styles from "./sellerOptionsMenu.module.scss";

export const SELLER_OPTIONS_TITLE = "All Seller Options";

/**
 * The "All Seller Options" drawer, the seller options table for small screens, where the page body leaves it
 * out. Opened from the buy box with `setOpenMenu("sellerOptions")`; it shows the offers of the product page
 * that is open, at the quantity picked in its sticky footer.
 */
export default function SellerOptionsMenu({ closeCallback }: OverlayMenuBaseProps) {
  const sellerOptions = useSellerOptions();
  const isSignedIn = useIsSignedIn();

  // The drawer belongs to the product page, so it closes when adding to cart or a seller link leaves it.
  const pathname = usePathname();
  const openedOn = useRef(pathname);
  useEffect(() => {
    if (pathname !== openedOn.current) {
      closeCallback?.();
    }
  }, [closeCallback, pathname]);

  if (sellerOptions === null) {
    return null;
  }

  const { offers, visibleOffers, filters, sort, setSort, clearFilters, quantity, setQuantity, isUpdating } =
    sellerOptions;

  return (
    <div className={styles.SellerOptionsMenu} data-seller-options-drawer="">
      <div className={styles.body}>
        {offers.length > 1 && (
          <div className={styles.controls}>
            <FilterMenu sellerOptions={sellerOptions} filterOrder={getFilterOrder(isSignedIn)} />
            <SellerOptionsSort sort={sort} onChange={setSort} disabled={visibleOffers.length === 0} />
          </div>
        )}
        <ResultCount count={visibleOffers.length} filtered={filters.size > 0} />
        {visibleOffers.length === 0 ? (
          <NoMatchingSellers onClearFilters={clearFilters} />
        ) : (
          <div className={styles.list} aria-busy={isUpdating}>
            <OfferList offers={visibleOffers} sellerOptions={sellerOptions} layout="drawer" />
            {isUpdating && (
              <div className={styles.updating}>
                <Preloader textUnderAnimation="Updating prices..." className={styles.preloader} />
              </div>
            )}
          </div>
        )}
      </div>
      <div className={styles.footer}>
        <span className={styles.quantityLabel} aria-hidden>
          Product Quantity:
        </span>
        <QuantityPicker
          value={quantity}
          onChange={setQuantity}
          ariaLabel="Product quantity"
          className={styles.quantityPicker}
        />
      </div>
    </div>
  );
}
